import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { APIService } from 'src/app/api/api.service';
import { Login, LoginWithOtp } from 'web-api-client/src/lib/generated/kia-authentication-api-client';
import { ToastrService } from 'ngx-toastr';
import { RoleEnum } from '../../enums/role.enum';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  twoFactorAuthForm: FormGroup;
  hasError: boolean = false;
  errorMessage: string = 'The login details are incorrect';
  returnUrl: string;
  isLoading: boolean = false;
  verify2fa: boolean = false;

  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private apiService: APIService,
    private changeDetectorRef: ChangeDetectorRef,
    private toastr: ToastrService
  ) {
    //this.isLoading$ = this.authService.isLoading$;
    // redirect to home if already logged in
    if (this.authService.currentUserValue) {
      this.router.navigate(['/']);
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.init2faForm();
    // get return url from route parameters or default to '/'
    this.returnUrl =
      this.route.snapshot.queryParams['returnUrl'.toString()] || '/';
    if (localStorage.getItem('userLoggedOut') == 'true') {
      this.toastr.success('Logged out successfully', 'Success');
      localStorage.removeItem('userLoggedOut');
    }
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  decodeJwt(token: string) {
    const parts = token.split('.');
    if (parts.length === 3) {
        const decoded = atob(parts[1]);
        return JSON.parse(decoded);
    }
    return {};
  }

  initForm() {
    this.loginForm = this.fb.group({
      email: [
        "",
        Validators.compose([
          Validators.required,
          Validators.email,
          Validators.minLength(3),
          Validators.maxLength(320), // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
        ]),
      ],
      password: [
        "",
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ]),
      ],
    });
  }

  init2faForm() {
    this.twoFactorAuthForm = this.fb.group({
      code: [
        "",
        Validators.compose([
          Validators.required,
          Validators.pattern('^[0-9]{6}$'),
        ]),
      ],
    });
  }

  submit() {
    this.errorMessage = 'The login details are incorrect';
    this.hasError = false;
    this.verify2fa = false;
    this.isLoading = true;
    const loginModel: Login = new Login();
    loginModel.email = this.loginForm.controls["email"].value;
    loginModel.password = this.loginForm.controls["password"].value;
    this.apiService.getKiaAuthenticationApiClient().login(loginModel).then((r) => {
      if (r.token) {
        const decodedToken = this.decodeJwt(r.token);
        const userRole = decodedToken["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

        this.authService.role = userRole!;
        this.apiService.setAuth(r.token!);
        this.apiService.setLoggedInUserEmail(loginModel.email);
        this.router.navigate(['/']);
      } else {
        this.verify2fa = true;
      }
    }).catch((err) => {
      this.hasError = true;
    }).finally(() => {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    });;
  }

  submit2fa() {
    this.hasError = false;
    const loginWithOtpModel: LoginWithOtp = new LoginWithOtp();
    loginWithOtpModel.email = this.loginForm.controls["email"].value;
    loginWithOtpModel.otp = this.twoFactorAuthForm.controls["code"].value;
    this.apiService.getKiaAuthenticationApiClient().loginWithOtp(loginWithOtpModel).then((r) => {
      const decodedToken = this.decodeJwt(r.token!);
      const userRole = decodedToken["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

      this.authService.role = userRole!;
      this.apiService.setAuth(r.token!);
      this.apiService.setLoggedInUserEmail(loginWithOtpModel.email);
      this.router.navigate(['/']);
    }).catch((err) => {
      this.hasError = true;
    }).finally(() => {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
